import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import localAuth from '../services/localAuth';
import { LOCAL_USER, isLegacyDevice } from '../services/capability';
import { isCloudAvailable } from '../services/cloudSync';

// ---------------------------------------------------------------------------
// AuthContext — the single auth surface for the whole app.
//
// Two modes:
//  - "clerk" (modern devices + key present): real Clerk loaded as a LAZY chunk
//    (ClerkShell.jsx) so its modern JS never enters the main bundle. This is
//    the Android 9 TV fix: legacy WebViews skip Clerk entirely and boot fine.
//  - "local" (legacy devices / no key): device-only session, zero network.
//
// Both modes expose the same shape:
//   { mode, user, isSignedIn, loading, cloudReady,
//     signInOpen, setSignInOpen, openSignIn, signOut, signInLocal }
// ---------------------------------------------------------------------------

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext) || {};

const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const clerkEnabled = Boolean(CLERK_KEY && CLERK_KEY.startsWith('pk_'));
const legacyDevice = isLegacyDevice();
const useClerkMode = clerkEnabled && !legacyDevice;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(useClerkMode ? null : LOCAL_USER);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [cloudReady, setCloudReady] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [clerkReady, setClerkReady] = useState(!useClerkMode);

  // ---------- local mode: hydrate session from localStorage ----------
  useEffect(() => {
    if (useClerkMode) return;
    const session = localAuth.getSession();
    if (session) {
      setUser(session);
      setIsSignedIn(true);
    } else {
      setUser(LOCAL_USER);
      setIsSignedIn(false);
    }
  }, [useClerkMode]);

  // Clerk shell publishes the signed-in user here (via prop callback below)
  const handleClerkUser = useCallback((clerkUser) => {
    setUser(clerkUser || null);
    setIsSignedIn(Boolean(clerkUser));
    setCloudReady(isCloudAvailable());
    setClerkReady(true);
  }, []);

  // ---------- clerk mode: lazy-load the Clerk chunk ----------
  const [ClerkShellComponent, setClerkShellComponent] = useState(null);
  const [clerkFailed, setClerkFailed] = useState(false);

  useEffect(() => {
    if (!useClerkMode) return undefined;
    let cancelled = false;
    import('./ClerkShell')
      .then((mod) => {
        if (!cancelled) setClerkShellComponent(() => mod.ClerkShell);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Clerk unavailable, continuing in local mode:', err);
          setClerkFailed(true);
          setClerkReady(true);
          setUser(LOCAL_USER);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [useClerkMode]);

  // ---------- actions ----------
  const signInLocal = useCallback(async (name) => {
    const session = await localAuth.signIn({ name });
    setUser(session || LOCAL_USER);
    setIsSignedIn(Boolean(session));
    return session;
  }, []);

  const signOut = useCallback(async () => {
    try {
      if (typeof window !== 'undefined' && window.__flexClerk && window.__flexClerk.signOut) {
        await window.__flexClerk.signOut();
      }
    } catch (e) {
      /* fall through to local cleanup */
    }
    await localAuth.signOut();
    setUser(LOCAL_USER);
    setIsSignedIn(false);
    setCloudReady(false);
  }, []);

  const openSignIn = useCallback(() => {
    if (useClerkMode && !clerkFailed) {
      setSignInOpen(true);
      return;
    }
    // Local mode fallback: lightweight device-only session (no network)
    const name = window.prompt ? window.prompt('Your display name (stored on this device only):') : '';
    if (name !== null && String(name).trim()) {
      signInLocal(String(name).trim());
    }
  }, [useClerkMode, clerkFailed, signInLocal]);

  const mode = useClerkMode && !clerkFailed ? 'clerk' : 'local';

  const value = useMemo(
    () => ({
      mode,
      user,
      isSignedIn,
      loading: useClerkMode && !clerkReady,
      cloudReady,
      signInOpen,
      setSignInOpen,
      openSignIn,
      signOut,
      signInLocal,
    }),
    [mode, user, isSignedIn, useClerkMode, clerkReady, cloudReady, signInOpen, openSignIn, signOut, signInLocal]
  );

  return (
    <AuthContext.Provider value={value}>
      {mode === 'clerk' && ClerkShellComponent ? (
        <ClerkShellComponent
          publishableKey={CLERK_KEY}
          onUser={handleClerkUser}
          signInOpen={signInOpen}
          onClose={() => setSignInOpen(false)}
        >
          {children}
        </ClerkShellComponent>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
